// biome-ignore lint/style/noRestrictedImports: analytics abstraction layer — sole posthog consumer
import posthog from "posthog-js";
import { getUrl } from "src/basepath";
import { getRouteObject } from "./appRoutes/appRoutes";

/** Known feature names for IDE autocompletion; callers may pass any string via `FeatureUseType`. */
export type KnownFeature =
  | "lineage_view"
  | "lineage_interaction"
  | "ai_assistant_message"
  | "ai_toolbox_view"
  | "insight_view"
  | "insight_interaction"
  | "system_settings"
  | "global_search"
  | "pipeline_settings"
  | "params_compare_drawer"
  | "fab_menu_open"
  | "fab_visibility_toggle"
  | "feedback_open"
  | "feedback_submit";

export type FeatureUseType = KnownFeature | (string & {});

export interface AnalyticsEvent {
  event: string;
  properties: Record<string, unknown>;
}

interface FeatureUseProperties {
  [key: string]: unknown;
}

/**
 * E2E test hook — when `window.__analyticsEvents` exists (injected by
 * `setupAnalyticsCollector` in e2e/utils/analyticsCollector.ts via
 * `page.addInitScript`), captured events are pushed into it so tests can
 * assert on analytics without requiring PostHog to be initialized.
 * In production the array is never created, so the optional-chain is a no-op.
 */
declare global {
  interface Window {
    __analyticsEvents?: AnalyticsEvent[];
  }
}

/** Derives page-level context attached to analytics events. */
function getPageContext() {
  const pathname = window.location.pathname;
  const route = getRouteObject(pathname);
  return {
    page_type: route?.key ?? pathname,
    page_type_known: !!route,
    page_url: pathname,
  };
}

/**
 * Initialize the analytics provider. Call once at app startup when
 * analytics is enabled.
 *
 * `before_send` enriches every event with page context. This is the only
 * hook for auto-fired events ($pageview, $pageleave, $autocapture, etc.)
 * since they bypass our `capture` wrapper. Custom events dispatched
 * through `captureFeature`/`captureError` also inject page context
 * at the call site so the E2E test collector (`window.__analyticsEvents`)
 * receives it — that array is populated before PostHog processes the event.
 */
export function initAnalytics(
  apiKey: string,
  options: Record<string, unknown>,
  onLoaded?: () => void
): void {
  // A relative api_host is the nginx reverse proxy, under the base path too.
  const apiHost = options.api_host;
  posthog.init(apiKey, {
    ...options,
    ...(typeof apiHost === "string" &&
      apiHost.startsWith("/") && {
        api_host: getUrl(apiHost),
      }),
    loaded: onLoaded,
    before_send: (event) => {
      if (!event) return null;

      Object.assign(event.properties, getPageContext());

      return event;
    },
  });
}

/** Capture a generic analytics event. */
export function capture(event: string, properties?: Record<string, unknown>): void {
  try {
    posthog.capture(event, properties);
    window.__analyticsEvents?.push({
      event,
      properties: properties ?? {},
    });
  } catch {
    console.error("analytics.capture failed:", event);
  }
}

/**
 * Associate the current session with a known user.
 * `propertiesSetOnce` maps to PostHog's third `identify` argument (properties set only once per person).
 */
export function identify(
  userId: string,
  properties?: Record<string, unknown>,
  propertiesSetOnce?: Record<string, unknown>
): void {
  try {
    posthog.identify(userId, properties, propertiesSetOnce);
  } catch {
    console.error("analytics.identify failed");
  }
}

/** Clear the current user identity (e.g. on logout). */
export function resetIdentity(): void {
  try {
    posthog.reset();
  } catch {
    console.error("analytics.resetIdentity failed");
  }
}

/**
 * Associate the session with a group (e.g. company / tenant).
 * Thin wrapper over `posthog.group` — exists solely to satisfy the biome
 * `noRestrictedImports` rule that bans direct `posthog-js` imports outside this file.
 * Not routed through `capture()` — not collected by `window.__analyticsEvents` for E2E.
 */
export function setGroup(
  groupType: string,
  groupKey: string,
  properties?: Record<string, unknown>
): void {
  try {
    posthog.group(groupType, groupKey, properties);
  } catch {
    console.error("analytics.setGroup failed");
  }
}

/**
 * Register super properties sent with every subsequent capture. Not routed through `capture()` —
 * not collected by `window.__analyticsEvents` for E2E.
 */
export function registerSuperProperties(properties: Record<string, unknown>): void {
  try {
    posthog.register(properties);
  } catch {
    console.error("analytics.registerSuperProperties failed");
  }
}

/**
 * Fires a "feature_use" analytics event with automatic page context.
 *
 * In non-prod builds the provider is not initialized so `capture` is a
 * silent no-op, but the `window.__analyticsEvents` hook still collects the
 * event for E2E assertions.
 */
export function captureFeature(
  feature: FeatureUseType,
  properties?: FeatureUseProperties
) {
  capture("feature_use", {
    feature,
    ...getPageContext(),
    ...properties,
  });
}

/**
 * Manually signal that a feature surface was closed / dismissed.
 * Place strategically at the exact point where "closed" is unambiguous —
 * e.g. a close-button handler, modal `onClose`, or drawer dismiss callback.
 *
 * PostHog computes duration server-side from the matching `feature_use` event's timestamp.
 *
 * Why a separate function instead of merging into `captureFeature(action, feature)`?
 * - Grepability: searching for `captureFeatureClose` finds every close-emitter directly.
 * - Intent clarity: `captureFeature` is the default "shown/used" signal; this is manual/strategic.
 * - Consistent with `captureError` — same internal shape, separate function per event type.
 * - Merge threshold: revisit only if a third lifecycle action appears (no evidence for one today).
 */
export function captureFeatureClose(
  feature: FeatureUseType,
  properties?: FeatureUseProperties
) {
  capture("feature_close", {
    feature,
    ...getPageContext(),
    ...properties,
  });
}

/** Short human-readable description of where/why the error occurred (e.g. "Login: sign in", "Dismiss alerts") */
export type ErrorSource = string;

function formatError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Fires an "error" analytics event for unexpected failures.
 *
 * Use only for genuine errors — not form validation, expected empty states,
 * or transient deployment-related chunk reloads (those auto-recover via reload).
 * The "chunk reload exhausted" case is a genuine error — the user is stuck.
 */
export function captureError(source: ErrorSource, error: unknown): void {
  capture("error", {
    error_source: source,
    error_message: formatError(error),
    error_type: error instanceof Error ? error.constructor.name : typeof error,
    ...getPageContext(),
  });
}
